import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
export function PricingSection() {
  const scrollToContact = () => {
    document.querySelector('#contacto')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const tiers = [{
    name: 'Obra',
    subtitle: 'Para una obra activa y un equipo operativo simple.',
    price: 'Desde 5 UF',
    unit: '/ mes',
    features: ['Historial completo de obra', 'Fotos, audios y mensajes respaldados', 'Búsqueda instantánea', 'Informes automáticos', 'Funciona con WhatsApp'],
    cta: 'Probar con una obra',
    highlighted: false
  }, {
    name: 'Multiobra',
    subtitle: 'Para subcontratos y empresas con varias obras activas.',
    price: 'Desde 10 UF',
    unit: '/ mes',
    features: ['Varias obras activas', 'Múltiples grupos de WhatsApp', 'Dashboard consolidado', 'Historial centralizado', 'Mayor capacidad operativa'],
    cta: 'Hablar con nosotros',
    highlighted: true,
    badge: 'Más usado'
  }, {
    name: 'Empresa',
    subtitle: 'Para operaciones intensivas y múltiples equipos.',
    price: 'Desde 50 UF',
    unit: null,
    features: ['Alto volumen de actividad', 'Equipos grandes', 'Configuración personalizada', 'Soporte prioritario', 'Integraciones futuras'],
    cta: 'Coordinar demo',
    highlighted: false
  }];
  return <section id="precio" className="scroll-mt-20 py-16 sm:py-24 bg-gray-50" data-id="element-183">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-184">
        <div className="text-center mb-12 sm:mb-16 max-w-3xl mx-auto" data-id="element-185">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 mb-3 sm:mb-4 tracking-tight" data-id="element-186">
            Precio fijo por obra.
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-gray-600" data-id="element-187">
            Sin preocuparte por usuarios o licencias.
          </p>
        </div>

        {/* Tiers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 mb-10 sm:mb-12 items-stretch" data-id="element-188">
          {tiers.map((tier, index) => {
          const isHighlighted = tier.highlighted;
          return <motion.div key={tier.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.08
          }} className={`relative rounded-2xl p-6 sm:p-8 flex flex-col shadow-sm ${isHighlighted ? 'bg-blue-600 border border-blue-700 shadow-lg md:scale-[1.02]' : 'bg-white border border-gray-200'}`} data-id="element-189">
                {tier.badge && <div className="absolute -top-3 left-1/2 -translate-x-1/2" data-id="element-190">
                    <span className="inline-block px-3 py-1 bg-gray-900 text-white text-[11px] font-bold uppercase tracking-wider rounded-full shadow-sm" data-id="element-191">
                      {tier.badge}
                    </span>
                  </div>}

                <h3 className={`text-xl font-bold mb-1.5 ${isHighlighted ? 'text-white' : 'text-gray-900'}`} data-id="element-192">
                  {tier.name}
                </h3>
                <p className={`text-sm leading-snug mb-6 min-h-[40px] ${isHighlighted ? 'text-blue-100' : 'text-gray-500'}`} data-id="element-193">
                  {tier.subtitle}
                </p>

                <div className="mb-6" data-id="element-194">
                  <div className={`text-3xl sm:text-4xl font-extrabold mb-0.5 leading-none ${isHighlighted ? 'text-white' : 'text-gray-900'}`} data-id="element-195">
                    {tier.price}
                  </div>
                  {tier.unit && <p className={`text-xs font-medium ${isHighlighted ? 'text-blue-200' : 'text-gray-400'}`} data-id="element-196">
                      {tier.unit}
                    </p>}
                </div>

                <ul className="space-y-2.5 mb-8 flex-1" data-id="element-197">
                  {tier.features.map(feature => <li key={feature} className="flex items-start gap-2.5" data-id="element-198">
                      <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isHighlighted ? 'text-white' : 'text-blue-600'}`} strokeWidth={3} data-id="element-199" />
                      <span className={`text-sm leading-snug ${isHighlighted ? 'text-blue-50' : 'text-gray-700'}`} data-id="element-200">
                        {feature}
                      </span>
                    </li>)}
                </ul>

                <button onClick={scrollToContact} className={`w-full py-3 px-5 rounded-xl text-sm font-bold transition-colors ${isHighlighted ? 'bg-white text-blue-600 hover:bg-blue-50 active:bg-blue-100' : 'bg-gray-900 text-white hover:bg-gray-800 active:bg-black'}`} data-id="element-201">
                  {tier.cta}
                </button>
              </motion.div>;
        })}
        </div>

        {/* Notes */}
        <div className="text-center max-w-2xl mx-auto mb-16 sm:mb-24 px-2" data-id="element-202">
          <p className="text-sm sm:text-base text-gray-700 font-medium leading-relaxed" data-id="element-203">
            El precio final depende del tamaño de obra, cantidad de grupos y
            nivel de actividad.
          </p>
          <p className="mt-2 text-xs sm:text-sm text-gray-400 italic" data-id="element-204">
            Una obra no necesita más chats. Necesita memoria.
          </p>
        </div>

        {/* Closing statement */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center px-4" data-id="element-205">
          <p className="text-xl sm:text-2xl lg:text-4xl font-extrabold text-gray-900 leading-tight tracking-tight max-w-3xl mx-auto" data-id="element-206">
            Cuando se pierde el contexto,{' '}
            <span className="text-blue-600" data-id="element-207">
              alguien termina pagando el costo.
            </span>
          </p>
        </motion.div>
      </div>
    </section>;
}